import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = n => '₦' + Number(n||0).toLocaleString('en-NG', {minimumFractionDigits:2});

const STATUS_COLORS = {
  pending:{ bg:'#F0F0F0', color:'#8A8A8A' },
  in_production:{ bg:'#FFF3DC', color:'#8B5E0A' },
  ready:{ bg:'#E8F5EB', color:'#2E6B3A' },
  dispatched:{ bg:'#556B2F22', color:'#556B2F' },
};

export default function JobCosting() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    get('/bills/costing-summary').then(d => setJobs(d.data||[])).finally(() => setLoading(false));
  };

  const loadDetail = async (jo) => {
    setSelected(jo);
    const d = await get('/bills/job-order/'+jo.id);
    setDetail(d);
  };

  const filtered = jobs.filter(j =>
    j.job_number?.toLowerCase().includes(search.toLowerCase()) ||
    j.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    j.product_description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>
            Job Costing
          </h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Track costs linked to each job order</p>
        </div>
        {selected && (
          <button className="btn btn-ghost" onClick={()=>{ setSelected(null); setDetail(null); }}>
            ← All Jobs
          </button>
        )}
      </div>

      {!selected ? (
        <>
          <input className="form-control" placeholder="Search job orders..."
            value={search} onChange={e=>setSearch(e.target.value)}
            style={{maxWidth:300,marginBottom:16,fontSize:12}} />

          {loading ? <div className="spinner"/> : (
            <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#2C1F16'}}>
                    {['Job #','Customer','Product','Qty','Total Costs','Status'].map(h=>(
                      <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                        color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} style={{padding:20,textAlign:'center',color:'#8A8A8A'}}>
                      No job orders found
                    </td></tr>
                  ) : filtered.map((jo,i)=>{
                    const st = STATUS_COLORS[jo.status]||STATUS_COLORS.pending;
                    return (
                      <tr key={jo.id} style={{background:i%2===0?'#fff':'#F9F8F5',cursor:'pointer'}}
                        onClick={()=>loadDetail(jo)}>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                          fontWeight:600,color:'#556B2F'}}>{jo.job_number}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>{jo.customer_name||'—'}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>{jo.product_description||'—'}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>{jo.quantity}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600,
                          color:parseFloat(jo.total_cost)>0?'#8F2E24':'#8A8A8A'}}>
                          {fmt(jo.total_cost)}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,
                            fontWeight:600,...st,textTransform:'uppercase'}}>
                            {jo.status?.replace(/_/g,' ')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:'#2C1F16'}}>
                    <td colSpan={4} style={{padding:'10px 14px',color:'#E8C466',
                      fontSize:11,fontWeight:700}}>TOTAL</td>
                    <td style={{padding:'10px 14px',color:'#E8C466',fontWeight:700}}>
                      {fmt(filtered.reduce((s,j)=>s+parseFloat(j.total_cost||0),0))}
                    </td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      ) : (
        <div>
          {/* Job header */}
          <div style={{background:'#3B2A21',borderRadius:12,padding:20,marginBottom:20,
            display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div>
              <div style={{color:'rgba(242,239,231,.5)',fontSize:11,marginBottom:4}}>
                {selected.job_number} · {selected.customer_name}
              </div>
              <div style={{color:'#A8893D',fontFamily:'Cormorant Garamond,serif',
                fontSize:'1.4rem',fontWeight:600}}>{selected.product_description}</div>
              <div style={{color:'rgba(242,239,231,.5)',fontSize:12,marginTop:4}}>
                Qty: {selected.quantity}
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{color:'rgba(242,239,231,.5)',fontSize:11,marginBottom:4}}>Total Cost</div>
              <div style={{color:'#E8C466',fontFamily:'Cormorant Garamond,serif',
                fontSize:'1.8rem',fontWeight:700}}>{fmt(detail?.total||selected.total_cost)}</div>
            </div>
          </div>

          {/* Bills linked */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',
              letterSpacing:'.08em',marginBottom:12}}>Linked Bills</div>
            {!detail ? <div className="spinner"/> : detail.bills.length === 0 ? (
              <div style={{background:'#F9F8F5',borderRadius:10,padding:20,
                textAlign:'center',fontSize:13,color:'#8A8A8A'}}>
                No bills linked to this job order yet.
                Link bills from the Bills page when creating or editing a bill.
              </div>
            ) : (
              <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#2C1F16'}}>
                      {['Bill #','Date','Supplier','Category','Amount','Status'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                          color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.bills.map((b,i)=>(
                      <tr key={b.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                          fontWeight:600,color:'#A8893D'}}>{b.bill_number}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {new Date(b.date).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric'})}
                        </td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>{b.supplier_name||'—'}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>{b.category}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                          fontWeight:600}}>{fmt(b.amount)}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          <span style={{fontSize:9,padding:'3px 8px',borderRadius:20,fontWeight:600,
                            background:b.status==='paid'?'#E8F5EB':b.status==='part_paid'?'#FFF3DC':'#F5F5F5',
                            color:b.status==='paid'?'#2E6B3A':b.status==='part_paid'?'#8B5E0A':'#8A8A8A'}}>
                            {b.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'#F9F8F5'}}>
                      <td colSpan={4} style={{padding:'10px 14px',fontWeight:700,fontSize:12}}>Total Bills</td>
                      <td style={{padding:'10px 14px',fontWeight:700,color:'#8F2E24'}}>
                        {fmt(detail.bills.reduce((s,b)=>s+parseFloat(b.amount||0),0))}
                      </td>
                      <td/>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Other costs */}
          {detail?.costs?.length > 0 && (
            <div>
              <div style={{fontSize:11,fontWeight:700,color:'#556B2F',textTransform:'uppercase',
                letterSpacing:'.08em',marginBottom:12}}>Other Costs</div>
              <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#2C1F16'}}>
                      {['Type','Description','Amount','Date'].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                          color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.costs.map((c,i)=>(
                      <tr key={c.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                          textTransform:'capitalize'}}>{c.cost_type}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>{c.description}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                          fontWeight:600}}>{fmt(c.amount)}</td>
                        <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                          {new Date(c.created_at).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
